// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { successResponseSchema } from '@/api/schemas';
import type {
  CreateWorkflowWithStagesRequest,
  UpdateWorkflowRequest,
  Workflow,
} from '@/workflow/api/types';
import {
  workflowResponseSchema,
  workflowsResponseSchema,
} from '@/workflow/api/types';

/**
 * Fetches all workflows for the current organization
 */
export async function getWorkflows(withCount?: boolean): Promise<Workflow[]> {
  const url = '/v1/workflow';
  const params = withCount ? { withCount: 'true' } : {};
  const response = await api.get(url, { params });
  const result = await parseResponse(workflowsResponseSchema, response.data, {
    schemaName: 'Workflows',
    endpoint: url,
  });
  return result.data;
}

/**
 * Creates a new workflow with stages
 */
export async function createWorkflowWithStages(
  data: CreateWorkflowWithStagesRequest
): Promise<Workflow> {
  const url = '/v1/workflow';
  const response = await api.post(url, data);
  const result = await parseResponse(workflowResponseSchema, response.data, {
    schemaName: 'CreateWorkflowWithStages',
    endpoint: url,
  });
  return result.data;
}

/**
 * Updates an existing workflow
 */
export async function updateWorkflow(
  workflowKey: string,
  data: UpdateWorkflowRequest
): Promise<Workflow> {
  const url = `/v1/workflow/${workflowKey}`;
  const response = await api.put(url, data);
  const result = await parseResponse(workflowResponseSchema, response.data, {
    schemaName: 'UpdateWorkflow',
    endpoint: url,
  });
  return result.data;
}

/**
 * Sets a workflow as the default workflow for the organization.
 * This will automatically unset any other workflow as default.
 */
export async function setWorkflowAsDefault(
  workflowKey: string
): Promise<Workflow> {
  const url = `/v1/workflow/${workflowKey}`;
  const response = await api.put(url, {
    isDefault: true,
  });
  const result = await parseResponse(workflowResponseSchema, response.data, {
    schemaName: 'SetWorkflowAsDefault',
    endpoint: url,
  });
  return result.data;
}

export async function deleteWorkflow(workflowKey: string): Promise<void> {
  const url = `/v1/workflow/${workflowKey}`;
  const response = await api.delete(url, { data: {} });

  await parseResponse(successResponseSchema, response.data, {
    schemaName: 'DeleteWorkflow',
    endpoint: url,
  });
}
